/**
 * Luli For Babies — Brindes progressivos no carrinho.
 *
 * Cada faixa de valor do pedido libera um brinde (ex.: R$199 → Fraldinha,
 * R$399 → Bolsinha térmica, …). O tema é quem coloca e tira os brindes do
 * carrinho e é o ÚNICO guardião dos valores mínimos: no admin, a promoção é um
 * desconto automático de 100% nos produtos-brinde SEM requisito mínimo (o
 * mínimo de um desconto de produto conta só os produtos selecionados, e o
 * "Compre X leve Y" nativo é suprimido quando um cupom entra no checkout).
 *
 * Regra, a cada `cart:update` / `discount:update` do tema:
 *
 *   subtotal = soma dos itens do carrinho (já com descontos), SEM os brindes
 *   faixas liberadas = faixas com mínimo <= subtotal (promoção dentro do prazo)
 *     modo "todos"  → a cliente ganha TODOS os brindes liberados
 *     modo "maior"  → só o brinde da maior faixa liberada
 *   brinde liberado e fora do carrinho   → entra (1 unidade, 1 linha)
 *   brinde NÃO liberado e no carrinho    → sai (o desconto não tem mínimo; sem
 *                                          esta remoção sairia grátis em
 *                                          qualquer pedido)
 *
 * O brinde entra com a propriedade de linha `_luli_brinde` (o tema não exibe
 * propriedades que começam com "_", ver cart-products.liquid), mas QUALQUER
 * linha de um produto-brinde é tratada como brinde, marcada ou não — os
 * produtos são dedicados a isso (fora de coleções e da busca).
 *
 * Se a cliente REMOVER um brinde de propósito (a linha some com a faixa ainda
 * liberada), respeitamos: sessionStorage registra a dispensa daquele brinde e
 * não o re-adicionamos nesta aba.
 *
 * Config: snippets/luli-free-gift.liquid (JSON #luli-free-gift-config), só
 * renderizado com a promoção ativa nas Configurações do tema.
 */
const SOURCE = 'luli-free-gift';
const GIFT_PROP = '_luli_brinde';
const ADDED_KEY = 'luli:brinde:added:';
const DISMISSED_KEY = 'luli:brinde:dismissed:';

let config = null;
let tiers = [];
let giftVariantIds = new Set();
let busy = false;
let pending = false;

/* sessionStorage pode estar indisponível (modo privado antigo); falha em silêncio. */
function setFlag(prefix, variantId, on) {
  try {
    const key = prefix + variantId;
    on ? sessionStorage.setItem(key, '1') : sessionStorage.removeItem(key);
  } catch (e) {
    /* sem storage, sem memória de dispensa — o pior caso é re-oferecer o brinde */
  }
}

function hasFlag(prefix, variantId) {
  try {
    return sessionStorage.getItem(prefix + variantId) === '1';
  } catch (e) {
    return false;
  }
}

/*
 * Marcada ou não: uma linha de produto-brinde adicionada por fora (URL direta,
 * outra aba) não tem a propriedade, mas obedece à mesma régua.
 */
function isGiftLine(item) {
  return giftVariantIds.has(item.variant_id) || Boolean(item.properties && item.properties[GIFT_PROP]);
}

function fetchCart() {
  return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => {
    /* erro da Ajax API vem como JSON ({status, message}) — sem este check ele
       passaria por um carrinho sem items e derrubaria o sync */
    if (!r.ok) throw new Error('cart.js ' + r.status);
    return r.json();
  });
}

/*
 * Mesma língua do product-form e do luli-wishlist: um `cart:update` sem
 * `sections` faz o cart-items-component refazer o próprio fetch e se
 * re-renderizar (drawer, totais, bolha do topo) — nada de montar HTML aqui.
 */
function announce(itemCount) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: { data: { source: SOURCE, itemCount } },
    })
  );
}

/* Todos os brindes novos numa única chamada (carrinho que pula de 0 a R$700 libera 3 de vez). */
async function addGifts(variantIds) {
  const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
  const properties = {};
  properties[GIFT_PROP] = '1';

  const res = await fetch(addUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({
      items: variantIds.map((id) => ({ id, quantity: 1, properties })),
    }),
  });
  if (!res.ok) throw new Error('cart/add ' + res.status);

  variantIds.forEach((id) => setFlag(ADDED_KEY, id, true));
}

/* quantity: 1 conserta uma quantidade forçada pela cliente; 0 remove a linha. */
async function setLineQuantity(lineKey, quantity) {
  const changeUrl = (window.Theme && Theme.routes && Theme.routes.cart_change_url) || '/cart/change.js';

  const res = await fetch(changeUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity }),
  });
  if (!res.ok) throw new Error('cart/change ' + res.status);
}

function isActive() {
  const now = Date.now();
  /* startsAt/endsAt em segundos, 0 = sem limite */
  if (config.startsAt && now < config.startsAt * 1000) return false;
  if (config.endsAt && now >= config.endsAt * 1000) return false;
  return true;
}

/*
 * A régua de brindes do carrinho (snippets/luli-gift-progress.liquid) é Liquid
 * e checa o período com 'now' — mas a página pode sair do cache do CDN (ex.:
 * carrinho vazio) com a decisão errada nos dois sentidos. Marcamos <html> com
 * .luli-gift-active / .luli-gift-inactive e o CSS do snippet dá a palavra
 * final ao relógio do navegador. Classe no <html> porque o morph da seção do
 * carrinho recria o DOM de dentro do <cart-items-component> a cada atualização
 * — um atributo posto na própria régua não sobreviveria.
 */
function reflectActive() {
  const active = isActive();
  document.documentElement.classList.toggle('luli-gift-active', active);
  document.documentElement.classList.toggle('luli-gift-inactive', !active);
}

/* Faixas liberadas pelo subtotal — todas, ou só a maior, conforme o modo. */
function unlockedVariantIds(subtotal) {
  if (!isActive()) return new Set();
  const unlocked = tiers.filter((tier) => subtotal >= tier.thresholdCents);
  if (config.mode === 'top') return new Set(unlocked.slice(-1).map((t) => t.variantId));
  return new Set(unlocked.map((t) => t.variantId));
}

async function sync() {
  /* Um sync por vez; eventos que chegarem no meio disparam UMA nova rodada no fim. */
  if (busy) {
    pending = true;
    return;
  }
  busy = true;

  try {
    reflectActive();
    const cart = await fetchCart();
    const subtotal = cart.items
      .filter((item) => !isGiftLine(item))
      .reduce((sum, item) => sum + item.final_line_price, 0);
    const wanted = unlockedVariantIds(subtotal);

    const toAdd = [];
    let changed = false;
    /* Variação esperada de item_count — fallback para a bolha do topo se o
       /cart.js final falhar (cart-icon interpreta undefined como 0). */
    let delta = 0;

    for (const variantId of giftVariantIds) {
      const lines = cart.items.filter((item) => item.variant_id === variantId);

      if (wanted.has(variantId)) {
        if (lines.length === 0) {
          if (hasFlag(DISMISSED_KEY, variantId)) {
            /* cliente tirou este brinde de propósito; não insistimos nesta aba */
          } else if (hasFlag(ADDED_KEY, variantId)) {
            /* estava lá e sumiu com a faixa ainda liberada → remoção manual */
            setFlag(DISMISSED_KEY, variantId, true);
            setFlag(ADDED_KEY, variantId, false);
          } else {
            toAdd.push(variantId);
          }
        } else {
          /* aba nova com brinde já no carrinho: registra, senão uma remoção
             manual aqui pareceria "nunca adicionado" e o brinde voltaria na hora */
          setFlag(ADDED_KEY, variantId, true);
          /* o desconto de 100% vale para TODA unidade do produto no carrinho —
             brinde é 1 unidade em 1 linha, e ponto */
          if (lines[0].quantity !== 1) {
            await setLineQuantity(lines[0].key, 1);
            delta += 1 - lines[0].quantity;
            changed = true;
          }
          for (const line of lines.slice(1)) {
            await setLineQuantity(line.key, 0);
            delta -= line.quantity;
            changed = true;
          }
        }
      } else if (lines.length > 0) {
        for (const line of lines) {
          await setLineQuantity(line.key, 0);
          delta -= line.quantity;
          changed = true;
        }
        setFlag(ADDED_KEY, variantId, false);
      }
    }

    /* Linhas marcadas como brinde de um produto que saiu da promoção (tema
       reconfigurado) também não podem ficar: sairiam de graça. */
    for (const line of cart.items) {
      if (!giftVariantIds.has(line.variant_id) && line.properties && line.properties[GIFT_PROP]) {
        await setLineQuantity(line.key, 0);
        delta -= line.quantity;
        changed = true;
      }
    }

    if (toAdd.length > 0) {
      await addGifts(toAdd);
      delta += toAdd.length;
      changed = true;
    }

    if (changed) {
      const fresh = await fetchCart().catch(() => null);
      const count = fresh && Number.isInteger(fresh.item_count) ? fresh.item_count : Math.max(0, cart.item_count + delta);
      announce(count);
    }
  } catch (error) {
    console.warn('[luli-free-gift]', error);
  } finally {
    busy = false;
    if (pending) {
      pending = false;
      sync();
    }
  }
}

function init() {
  const configEl = document.getElementById('luli-free-gift-config');
  if (!configEl) return;

  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }
  if (!config || !Array.isArray(config.tiers)) return;

  tiers = config.tiers
    .filter((tier) => tier && tier.variantId && tier.thresholdCents > 0)
    .sort((a, b) => a.thresholdCents - b.thresholdCents);
  if (tiers.length === 0) return;

  giftVariantIds = new Set(tiers.map((tier) => tier.variantId));
  reflectActive();

  document.addEventListener('cart:update', (event) => {
    const source = event.detail && event.detail.data && event.detail.data.source;
    if (source === SOURCE) return; // nosso próprio anúncio; o estado já está coerente
    sync();
  });
  /* cupom aplicado no drawer muda os preços finais — as faixas podem virar */
  document.addEventListener('discount:update', () => sync());

  /* Checagem inicial só quando já existe carrinho (ex.: voltou ao site com o
     carrinho cheio de ontem) — e fora do caminho crítico de carregamento. */
  if (config.itemCount > 0) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => sync(), { timeout: 3000 });
    } else {
      setTimeout(sync, 1500);
    }
  }
}

init();
